import { AdvHeading, AdvPanel, AdvSection, AdvUrgent, AdventureHero, Btn, DisplayTitle, Eyebrow, IconCardGrid, MonoRow, QuoteMarquee, Spotlight, StatRow, TerrainTiles, appUrl, useText } from '@kkf/design'
import { LINKS } from '../data/links'
import {
  GARAGE_BAND,
  GARAGE_HERO,
  GARAGE_HOME_INTRO,
  GARAGE_LOVE,
  GARAGE_NEED,
  GARAGE_PROGRAMS,
  GARAGE_SERVICES,
  GARAGE_SPECS,
  GARAGE_TERRAIN,
  GARAGE_TESTIMONIES,
  GARAGE_TRAILS,
} from '../data/garage'
import { TOURS } from '../data/tours'
import { useT } from '../i18n'

/**
 * The garage's home page.
 *
 * **Rebuilt twice, for two different reasons, and the second rebuild kept the first's
 * answer.**
 *
 * The first pass (2026-09-02) fixed the ORDER, against the note that the garage was
 * "looking boring, and the same as the others". It was: four routes of headings and card
 * grids, structurally identical to the resort's and Partners' in a browner palette.
 * The fix was to lead with the trail, put three bookable tours second, and let the
 * argument the page used to open with — a workshop that pays for a youth programme —
 * arrive once the visitor has a reason to care about it.
 *
 * That was right and it was not enough, because the REGISTER was untouched: pale linen
 * bands, a fleuron, `SectionHeading` over `HighlightGrid`. Correct for a foundation, wrong
 * for a workshop that recovers trucks two hundred kilometres from anywhere. So this pass
 * changes the shapes and keeps the order, which is why the section sequence below still
 * reads the way the first rebuild left it.
 *
 * ── The rhythm ────────────────────────────────────────────────────────────────────────
 *
 * There are no alternating grounds here. The page is one continuous near-black, and what
 * tells the eye where an idea stops is the GLOW — a radial wash of the display accent,
 * placed differently on each section. **No two adjacent sections may share a placement**;
 * two lit the same way read as one column, which is exactly the failure the pale layout's
 * `Band` tones were built to prevent. The sequence is:
 *
 *   hero → tiles(bleed) → tl → tr → signal(urgent) → bl → tr → band → br → tl → bl
 *
 * The one section that breaks the pattern deliberately is the recovery band: it takes the
 * INTERFACE accent's glow rather than the display accent's, because it is the only urgent
 * thing on the page and there is exactly one per page.
 */
/**
 * Splits a title for a two-tone display line: everything but the last word in the page's
 * own ink, the last word in the display accent.
 *
 * It exists because `name.split(' ').slice(-1)[0]` is `string | undefined` under
 * `noUncheckedIndexedAccess`, and scattering non-null assertions through a page to work
 * around a strictness setting is how a real empty-string bug gets hidden later. A
 * single-word title returns an empty tail, and `DisplayTitle` renders no accent span for
 * it — which is correct: there is nothing to put in the second tone.
 */
function twoTone(title: string): { head: string; tail?: string } {
  const words = title.trim().split(/\s+/)
  if (words.length < 2) return { head: title }
  return { head: words.slice(0, -1).join(' '), tail: words[words.length - 1] }
}

export function HomePage() {
  const t = useT()
  const s = useText()
  const intro = GARAGE_HOME_INTRO
  // `TOURS` is a hand-written module, so this is only ever undefined if somebody empties
  // it. The section is skipped rather than asserted — a spotlight with no tour in it is a
  // worse outcome than one missing section.
  const flagship = TOURS[0]

  return (
    <>
      <AdventureHero
        image={intro.banner}
        imageAlt={s(intro.bannerAlt)}
        pill={
          <>
            {s(GARAGE_HERO.pillLead)} — <b>{s(GARAGE_HERO.pillEmphasis)}</b>
          </>
        }
        /* Two-tone: the owner in the page's own ink, the venture in the display accent.
           It read RADICAL ZONE until 2026-09-02 — the last place that name survived, and
           the one a grep for "Radical Zone" could not find, because the string is split
           across two props. */
        title="KKF"
        titleAccent="Garage"
        coords={s(GARAGE_HERO.coords)}
        lead={s(intro.descr)}
        stats={GARAGE_HERO.stats.map((stat) => ({ ...stat, label: s(stat.label) }))}
      >
        <Btn to={intro.primaryCta.to} className="ul-btn--signal">
          {s(intro.primaryCta.label)}
        </Btn>
        <Btn to={intro.secondaryCta.to} className="ul-btn--ghost">
          {s(intro.secondaryCta.label)}
        </Btn>
      </AdventureHero>

      {/* THREE DOORS — full bleed, no container, no glow. The photographs are the whole
          width here, which is what stops a dark page of bordered cards reading as a
          dashboard. It butts against the hero, so it takes no top padding. */}
      <AdvSection bleed flush flushBottom>
        <TerrainTiles
          items={GARAGE_TERRAIN.map((tile) => ({
            to: tile.to,
            image: tile.image,
            kicker: s(tile.kicker),
            title: s(tile.title),
            linkLabel: s(tile.linkLabel),
            imageAlt: s(tile.imageAlt),
          }))}
        />
      </AdvSection>

      {/* THE TRAIL PROMISE — glow tl. The reason somebody who is not broken down is on
          this site at all, so it comes first after the doors. */}
      <AdvSection glow="tl" edged containerClassName="wow animate__fadeInUp">
        <AdvHeading
          centered
          eyebrow={s(GARAGE_TRAILS.subTitle)}
          title={t('home.trail')}
          titleAccent={t('home.trailAccent')}
          lead={s(GARAGE_TRAILS.lead)}
        />
      </AdvSection>

      {/* THE FLAGSHIP — glow tr. One tour, at spotlight scale, with its specs. The pale
          layout put three cards here; one item with its numbers on show is what the
          reference does and it is a better answer, because a card cannot carry a spec
          grid and the decision a visitor is making needs one.

          `flagship.pending` is true while the durations and prices are placeholders, and
          `SpecGrid` renders every cell as "to be confirmed" rather than inventing a
          figure — a made-up price on a booking page is a quotation, not a placeholder. */}
      {flagship && (
        <AdvSection glow="tr" containerClassName="wow animate__fadeInUp">
          <Spotlight
            image={flagship.image}
            imageAlt={s(flagship.imageAlt)}
            eyebrow={flagship.kind}
            // Split in the reader's language: the two-tone title cuts the tour's own
            // name at its last word, and where that falls differs per language.
            title={twoTone(s(flagship.name)).head}
            titleAccent={twoTone(s(flagship.name)).tail}
            claim={s(flagship.tagline)}
            lead={flagship.paragraphs[0] ? s(flagship.paragraphs[0]) : ''}
            specs={flagship.specs.map((spec) => ({
              label: s(spec.term),
              value: s(spec.detail),
              pending: flagship.pending,
            }))}
          >
            <Btn to={`/tours/${flagship.slug}`} className="ul-btn--signal">
              View the tour
            </Btn>
            <Btn to={GARAGE_TRAILS.ctaTo} className="ul-btn--ghost">
              {s(GARAGE_TRAILS.ctaLabel)}
            </Btn>
          </Spotlight>
        </AdvSection>
      )}

      {/* OFF THE ROAD? — the signal glow, and the only place on the page it appears. The
          customer this band is for is not reading: they are looking for a number. */}
      <AdvSection glow="signal" tight edged containerClassName="wow animate__fadeInUp">
        <AdvUrgent
          eyebrow={s(GARAGE_BAND.subTitle)}
          title={t('band.recovery')}
          titleAccent={t('band.recoveryAccent')}
          note={s(GARAGE_BAND.note)}
        >
          <Btn to={GARAGE_BAND.ctaTo} className="ul-btn--signal">
            {s(GARAGE_BAND.ctaLabel)}
          </Btn>
        </AdvUrgent>
      </AdvSection>

      {/* WHAT WE DO — glow bl. Four of the six services as bordered cards, which is the
          reference's "made by riders, for riders" shape. */}
      <AdvSection glow="bl" containerClassName="wow animate__fadeInUp">
        <AdvHeading
          eyebrow={t('home.servicesEyebrow')}
          title={t('home.builtIn')}
          titleAccent={t('home.builtInAccent')}
          aside={
            <Btn to="/services" className="ul-btn--ghost">
              The full service list
            </Btn>
          }
        />
        {/* Image-topped, matching `/services`. The icon font is `flaticon_charitics` and
            has no wrench, spanner or vehicle in it, so a photograph of the actual bench
            says more than the nearest mismatched glyph did. */}
        <IconCardGrid
          items={GARAGE_SERVICES.slice(0, 4).map((service) => ({
            title: s(service.title),
            descr: s(service.descr),
            image: service.image,
            imageAlt: s(service.imageAlt),
          }))}
        />
        {/* The remaining two, named rather than given a card each — the reference's way of
            stating the rest of a range without expanding it. */}
        <MonoRow items={GARAGE_SERVICES.slice(4).map((service) => s(service.title))} />
      </AdvSection>

      {/* WHAT WE CAN DO ON SITE — glow tr. The facts a customer wants before trusting a
          workshop they have never seen, as a spec grid rather than a definition list. */}
      <AdvSection glow="tr" containerClassName="wow animate__fadeInUp">
        <Spotlight
          reverse
          image="/assets/img/garage-workshop-lift.jpg"
          imageAlt="A vehicle raised on a workshop lift, tools and a bench behind it"
          eyebrow={t('home.workshopEyebrow')}
          title={t('home.canDo')}
          titleAccent={t('home.canDoAccent')}
          /* A SHEET, not a grid. These six values are sentences — "4x4s, light trucks,
             off-road and adventure motorcycles." — and `SpecGrid` sets its values in the
             display face at heading size, which turned them into a wall of 26px uppercase
             prose on the first draft of this page. The rule is in `SpecSheet`'s docstring:
             a figure is a grid, a sentence is a sheet. */
          sheet={GARAGE_SPECS.map((spec) => ({ label: s(spec.term), values: [s(spec.detail)] }))}
        >
          <Btn to="/contact" className="ul-btn--signal">
            Talk to the workshop
          </Btn>
        </Spotlight>
      </AdvSection>

      {/* WHERE THE MONEY GOES — glow br. Now that a visitor has a reason to care, this is
          what their booking pays for. Three of the eight programmes; the rest are on
          `/programmes`. */}
      <AdvSection glow="br" containerClassName="wow animate__fadeInUp">
        <AdvHeading
          centered
          eyebrow={s(GARAGE_LOVE.subTitle)}
          title={t('home.love')}
          titleAccent={t('home.loveAccent')}
          lead={s(GARAGE_LOVE.lead)}
        />
        <IconCardGrid
          cols={3}
          items={GARAGE_PROGRAMS.slice(0, 3).map((p) => ({
            title: s(p.title),
            descr: s(p.descr),
            image: p.image,
            imageAlt: s(p.imageAlt),
          }))}
        />
        <StatRow items={GARAGE_LOVE.points.map((p) => ({ figure: s(p.name), label: s(p.note) }))} />
      </AdvSection>

      {/* WHY IT MATTERS — glow tl. The UNICEF finding, attributed, beside the programmes
          it justifies rather than buried in a donor document. */}
      <AdvSection glow="tl" containerClassName="wow animate__fadeInUp">
        <div className="row row-cols-md-2 row-cols-1 gy-4 ul-bs-row align-items-center">
          <div className="col">
            <Eyebrow>{s(GARAGE_NEED.subTitle)}</Eyebrow>
            <DisplayTitle size="sm" accent={t('programmes.trajectoryAccent')}>
              The Trajectory
            </DisplayTitle>
            {GARAGE_NEED.paragraphs.map((p) => (
              <p className="ul-adv-lead" key={s(p)}>
                {s(p)}
              </p>
            ))}
          </div>
          <div className="col">
            <AdvPanel>
              <DisplayTitle size="sm" as="h3">
                {GARAGE_NEED.stat.figure}
              </DisplayTitle>
              <p className="ul-adv-lead">{GARAGE_NEED.stat.txt}</p>
              {/* The attribution is the reason this number is usable at all. */}
              <p className="ul-adv-lead">
                <em>{GARAGE_NEED.stat.source}</em>
              </p>
            </AdvPanel>
          </div>
        </div>
      </AdvSection>

      {/* FROM THE FLOOR — glow bl, and the marquee.
          The three testimonies keep "testimony to be recorded" in their attribution.
          They are written placeholders, not recorded testimony, and an open blocker on
          the garage's PRD — a louder frame is not permission to make them sound real. */}
      <AdvSection glow="bl" containerClassName="wow animate__fadeInUp">
        <AdvHeading centered eyebrow={t('home.wordsEyebrow')} title={t('home.from')} titleAccent={t('home.madeInAccent')} />
      </AdvSection>
      <AdvSection flush glow="none">
        <QuoteMarquee
          rows={[
            // `by` and `note` are placeholders on every entry — each says "testimony to
            // be recorded", which `CLAUDE.md` requires be labelled rather than hidden.
            GARAGE_TESTIMONIES.map((entry) => ({
              quote: s(entry.quote),
              by: s(entry.name),
              note: s(entry.role),
            })),
            [...GARAGE_TESTIMONIES].reverse().map((entry) => ({
              quote: s(entry.quote),
              by: s(entry.name),
              note: s(entry.role),
            })),
          ]}
        />
      </AdvSection>

      {/* ONE FOUNDATION, FOUR DOORS — glow tr. The cross-app panel. The footer's
          wayfinding row still carries these; this is a second, louder door, and it is
          where a visitor who came for a trail finds out there is a foundation behind it. */}
      <AdvSection glow="tr" containerClassName="wow animate__fadeInUp">
        <AdvHeading
          eyebrow={t('home.kkfEyebrow')}
          title={t('home.oneFoundation')}
          titleAccent={t('home.fourDoors')}
        />
        <AdvPanel
          url="kkfmongolia.com"
          links={[
            { label: 'The Foundation', href: LINKS.foundation },
            { label: 'The Resort', href: LINKS.resort },
            { label: 'King of Kings Partners', href: LINKS.partners },
            { label: 'Donate', href: appUrl(LINKS, 'foundation', '/donations') },
          ]}
        >
          <DisplayTitle size="sm" as="h3" accent="Foundation">
            King of Kings
          </DisplayTitle>
          <p className="ul-adv-lead">
            The Garage is one of four. The Foundation carries the story and the seven areas of
            work; the resort has the beds and the venues; Partners is the businesses that back
            all of it. Every one of them is a separate site on its own domain.
          </p>
        </AdvPanel>
      </AdvSection>
    </>
  )
}
